import { action } from 'mobx'
import type { FilterProps } from '@/infra/type-utils'

/**
 * Binds the model to the input.
 *
 * @param model
 * @param field
 */
export function bindInput<T, K extends keyof FilterProps<T, string>>(
  this: void,
  model: T,
  field: K,
) {
  return {
    value: model[field] as string,
    onChange: action(`change ${field}`, (newValue: string) => {
      // We've asserted that we can only
      // specify string property names.
      model[field] = newValue as T[K]
    }),
  }
}

/**
 * Codec for mapping between strings and other types when binding to inputs.
 */
export interface BindCodec<T> {
  /**
   * Decodes the model value from a string.
   * @param value
   */
  decode(value: string | null): T

  /**
   * Encodes the model value to a string.
   * @param value
   */
  encode(value: T): string
}

/**
 * Common codecs.
 */
export const BindCodecs = {
  /**
   * Codec for numbers.
   */
  number: {
    encode: (value) => value.toString(10),
    decode: (value) => (value === null ? 0 : parseInt(value, 10)),
  } as BindCodec<number>,
}

/**
 * Binds the model to the input.
 *
 * @param model
 * @param field
 * @param codec
 */
export function bindSelect<
  T,
  K extends keyof FilterProps<T, string | null>,
  C extends BindCodec<unknown>,
>(this: void, model: T, field: K, codec?: C) {
  const value = model[field] as string
  return {
    value: codec ? codec.encode(value) : value,
    onChange: action(`change ${field}`, (newValue: string | null) => {
      // We've asserted that we can only
      // specify string property names.
      model[field] = codec
        ? (codec.decode(newValue) as T[K])
        : (newValue as T[K])
    }),
  }
}
